import { motion, useInView } from "framer-motion";
import { useRef, useState } from "react";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from "@/components/ui/accordion";
import {
  ArrowRight,
  Check,
  Quote,
  MessageSquare,
  FileText,
  Ruler,
  Hammer,
  CheckCircle,
  Search,
  Star,
  Phone,
  Mail,
  MapPin,
  Clock,
} from "lucide-react";
import { toast } from "sonner";

/* ===== COLOR CONFIGS ===== */
const colorConfigs = {
  blue: {
    gradient: "gradient-blue",
    gradientSubtle: "gradient-blue-subtle",
    text: "text-brand-blue-light",
    textGradient: "text-gradient-blue",
    border: "border-brand-blue/20",
    borderSolid: "border-brand-blue/40",
    glow: "glow-blue",
    badge: "bg-brand-blue-muted text-brand-blue-light",
    btnVariant: "blue",
    btnOutline: "blue-outline",
    line: "bg-brand-blue",
    borderLeft: "border-l-brand-blue",
  },
  green: {
    gradient: "gradient-green",
    gradientSubtle: "gradient-green-subtle",
    text: "text-brand-green-light",
    textGradient: "text-gradient-green",
    border: "border-brand-green/20",
    borderSolid: "border-brand-green/40",
    glow: "glow-green",
    badge: "bg-brand-green-muted text-brand-green-light",
    btnVariant: "green",
    btnOutline: "green-outline",
    line: "bg-brand-green",
    borderLeft: "border-l-brand-green",
  },
  orange: {
    gradient: "gradient-orange",
    gradientSubtle: "gradient-orange-subtle",
    text: "text-brand-orange-light",
    textGradient: "text-gradient-orange",
    border: "border-brand-orange/20",
    borderSolid: "border-brand-orange/40",
    glow: "glow-orange",
    badge: "bg-brand-orange-muted text-brand-orange-light",
    btnVariant: "orange",
    btnOutline: "orange-outline",
    line: "bg-brand-orange",
    borderLeft: "border-l-brand-orange",
  },
};

/* ===== SUBPAGE HERO ===== */
export const SubpageHero = ({ color, title, subtitle, description, image }) => {
  const c = colorConfigs[color];
  const defaultStats = [
    { value: "25+", label: "Jahre Erfahrung" },
    { value: "100+", label: "Projekte" },
    { value: "100%", label: "Zufriedenheit" },
  ];

  return (
    <section className="relative pt-20 min-h-[85vh] flex flex-col overflow-hidden">
      {/* Background image - right side visible */}
      <div className="absolute inset-0">
        <div
          className="absolute inset-0 bg-cover bg-center"
          style={{ backgroundImage: `url(${image})` }}
        />
        <div className="absolute inset-0 bg-gradient-to-r from-background via-background/90 to-background/50" />
        <div className="absolute inset-0 bg-gradient-to-t from-background via-transparent to-background/70" />
      </div>

      {/* Top accent line */}
      <div className={`absolute top-0 left-0 right-0 h-1 ${c.gradient} z-20`} />

      {/* Decorative elements */}
      <div className="absolute inset-0 pointer-events-none overflow-hidden z-[1]">
        <div className="absolute top-0 left-1/4 w-px h-full bg-white/5" />
        <div className="absolute top-0 left-2/4 w-px h-full bg-white/5" />
        <div className="absolute top-0 left-3/4 w-px h-full bg-white/5" />
        {/* Large decorative number */}
        <div className={`absolute -right-10 top-1/4 font-heading font-bold text-[20rem] leading-none ${c.text} opacity-[0.03] select-none`}>
          {title.charAt(0)}
        </div>
      </div>

      {/* Main content */}
      <div className="relative z-10 container-wide flex-1 flex items-center py-16 md:py-24">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 items-center w-full">
          {/* Left content */}
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.7 }}
            className="lg:col-span-7"
          >
            <div className="flex items-center gap-4 mb-8">
              <div className={`w-12 h-1.5 ${c.gradient}`} />
              <span className="text-xs font-heading uppercase tracking-widest text-muted-foreground">
                {subtitle}
              </span>
            </div>
            <h1 className="font-heading font-bold text-4xl sm:text-5xl lg:text-6xl xl:text-7xl leading-[1.1] text-foreground mb-6">
              {title}
              <span className={`block ${c.text} text-3xl sm:text-4xl lg:text-5xl mt-2`}>Freitag-Gruppe</span>
            </h1>
            <p className="text-base md:text-lg text-muted-foreground font-body max-w-xl leading-relaxed mb-10">
              {description}
            </p>
            <div className="flex flex-wrap gap-4">
              <Button variant={c.btnVariant} size="xl" asChild>
                <a href="#kontakt">
                  Jetzt Anfrage stellen
                  <ArrowRight className="w-4 h-4" />
                </a>
              </Button>
              <Button variant={c.btnOutline} size="xl" asChild>
                <a href="#leistungen">Leistungen ansehen</a>
              </Button>
            </div>
          </motion.div>

          {/* Right side - stats card */}
          <motion.div
            initial={{ opacity: 0, x: 40 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ duration: 0.7, delay: 0.3 }}
            className="lg:col-span-5 hidden lg:block"
          >
            <div className="bg-card/60 backdrop-blur-md border border-white/10 p-8">
              <div className={`h-1 ${c.gradient} mb-6`} />
              <div className="space-y-6">
                {defaultStats.map((stat, i) => (
                  <div key={stat.label} className="flex items-center gap-4">
                    <span className={`font-heading font-bold text-3xl ${c.text}`}>{stat.value}</span>
                    <div className="flex-1 h-px bg-border" />
                    <span className="text-xs text-muted-foreground font-body uppercase tracking-wider">{stat.label}</span>
                  </div>
                ))}
              </div>
            </div>
          </motion.div>
        </div>
      </div>

      {/* Bottom stats bar - mobile only */}
      <div className="relative z-10 border-t border-white/10 bg-card/80 backdrop-blur-md lg:hidden">
        <div className="container-wide py-6">
          <div className="grid grid-cols-3 gap-8">
            {defaultStats.map((stat) => (
              <div key={stat.label} className="text-center">
                <p className={`font-heading font-bold text-2xl ${c.text}`}>{stat.value}</p>
                <p className="text-xs text-muted-foreground font-body mt-1">{stat.label}</p>
              </div>
            ))}
          </div>
        </div>
      </div>
    </section>
  );
};

/* ===== SUBPAGE SERVICES ===== */
export const SubpageLeistungen = ({ color, services }) => {
  const ref = useRef(null);
  const isInView = useInView(ref, { once: true, margin: "-80px" });
  const c = colorConfigs[color];

  return (
    <section className="section-padding bg-background" ref={ref} id="leistungen">
      <div className="container-wide">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={isInView ? { opacity: 1, y: 0 } : {}}
          transition={{ duration: 0.5 }}
          className="mb-16 max-w-2xl"
        >
          <span className="text-xs font-heading uppercase tracking-widest text-muted-foreground mb-3 block">
            Unsere Leistungen
          </span>
          <h2 className="font-heading font-bold text-3xl sm:text-4xl lg:text-5xl text-foreground mb-4 leading-tight">
            Was wir f{"u\u0308"}r Sie leisten k{"o\u0308"}nnen.
          </h2>
          <p className="text-sm text-muted-foreground font-body leading-relaxed">
            Unser umfassendes Leistungsspektrum deckt alle Bereiche ab, die Sie f{"u\u0308"}r Ihr Projekt ben{"o\u0308"}tigen.
          </p>
        </motion.div>

        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-px bg-border">
          {services.map((service, index) => {
            const Icon = service.icon;
            return (
              <motion.div
                key={service.title}
                initial={{ opacity: 0, y: 30 }}
                animate={isInView ? { opacity: 1, y: 0 } : {}}
                transition={{ duration: 0.4, delay: index * 0.1 }}
              >
                <Card className="border-0 bg-card h-full flex flex-col">
                  <CardContent className="p-6 md:p-8 flex flex-col flex-1">
                    <div className={`inline-flex items-center justify-center w-10 h-10 ${c.gradientSubtle} border ${c.border} mb-5`}>
                      <Icon className={`w-5 h-5 ${c.text}`} />
                    </div>
                    <h3 className="font-heading font-semibold text-base text-foreground mb-2">
                      {service.title}
                    </h3>
                    <p className="text-sm text-muted-foreground font-body leading-relaxed">
                      {service.description}
                    </p>
                  </CardContent>
                </Card>
              </motion.div>
            );
          })}
        </div>

        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={isInView ? { opacity: 1, y: 0 } : {}}
          transition={{ duration: 0.5, delay: 0.5 }}
          className="mt-12 bg-card border border-border p-8 md:p-10 flex flex-col md:flex-row items-center gap-6"
        >
          <Search className={`w-8 h-8 ${c.text} shrink-0`} />
          <div className="flex-1">
            <h3 className="font-heading font-semibold text-base text-foreground mb-1">
              Suchen Sie eine spezielle Leistung?
            </h3>
            <p className="text-sm text-muted-foreground font-body">
              Kontaktieren Sie uns direkt - wir finden die passende L{"o\u0308"}sung.
            </p>
          </div>
          <Button variant={c.btnVariant} size="default" asChild>
            <a href="#kontakt">
              Anfrage stellen
              <ArrowRight className="w-4 h-4" />
            </a>
          </Button>
        </motion.div>
      </div>
    </section>
  );
};

/* ===== SUBPAGE TEAM ===== */
export const SubpageTeam = ({ color, members }) => {
  const ref = useRef(null);
  const isInView = useInView(ref, { once: true, margin: "-80px" });
  const c = colorConfigs[color];

  return (
    <section className="section-padding bg-card" ref={ref}>
      <div className="container-wide">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={isInView ? { opacity: 1, y: 0 } : {}}
          transition={{ duration: 0.5 }}
          className="mb-16"
        >
          <span className="text-xs font-heading uppercase tracking-widest text-muted-foreground mb-3 block">
            Unser Team
          </span>
          <h2 className="font-heading font-bold text-3xl sm:text-4xl lg:text-5xl text-foreground mb-4 leading-tight">
            Kompetenz die {"u\u0308"}berzeugt.
          </h2>
        </motion.div>

        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-px bg-border">
          {members.map((member, index) => (
            <motion.div
              key={member.name}
              initial={{ opacity: 0, y: 20 }}
              animate={isInView ? { opacity: 1, y: 0 } : {}}
              transition={{ duration: 0.4, delay: index * 0.1 }}
            >
              <Card className="border-0 bg-background h-full">
                <div className={`h-0.5 ${c.gradient}`} />
                <CardContent className="p-6 md:p-8">
                  <div className={`w-14 h-14 ${c.gradientSubtle} border ${c.border} flex items-center justify-center mb-5`}>
                    <span className={`font-heading font-bold text-base ${c.text}`}>
                      {member.initials}
                    </span>
                  </div>
                  <h3 className="font-heading font-semibold text-base text-foreground mb-1">
                    {member.name}
                  </h3>
                  <p className={`text-sm font-body ${c.text} mb-1`}>
                    {member.role}
                  </p>
                  <p className="text-xs text-muted-foreground font-body">
                    {member.description}
                  </p>
                </CardContent>
              </Card>
            </motion.div>
          ))}
        </div>
      </div>
    </section>
  );
};

/* ===== SUBPAGE PROCESS ===== */
export const SubpageProzess = ({ color }) => {
  const ref = useRef(null);
  const isInView = useInView(ref, { once: true, margin: "-80px" });
  const c = colorConfigs[color];

  const steps = [
    { icon: MessageSquare, title: "Beratung", desc: "Erstberatung und Bedarfsanalyse.", number: "01" },
    { icon: FileText, title: "Angebot", desc: "Transparente Kalkulation.", number: "02" },
    { icon: Ruler, title: "Planung", desc: "Projektplanung mit Meilensteinen.", number: "03" },
    { icon: Hammer, title: "Umsetzung", desc: "Fachgerechte Ausf\u00fchrung.", number: "04" },
    { icon: CheckCircle, title: "Abnahme", desc: "Endabnahme und \u00dcbergabe.", number: "05" },
  ];

  return (
    <section className="section-padding bg-background" ref={ref}>
      <div className="container-wide">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={isInView ? { opacity: 1, y: 0 } : {}}
          transition={{ duration: 0.5 }}
          className="mb-16"
        >
          <span className="text-xs font-heading uppercase tracking-widest text-muted-foreground mb-3 block">
            Unser Prozess
          </span>
          <h2 className="font-heading font-bold text-3xl sm:text-4xl lg:text-5xl text-foreground mb-4 leading-tight">
            {"In f\u00fcnf Schritten zum Ziel."}
          </h2>
        </motion.div>

        <div className="grid grid-cols-1 md:grid-cols-5 gap-px bg-border">
          {steps.map((step, index) => {
            const Icon = step.icon;
            return (
              <motion.div
                key={step.title}
                initial={{ opacity: 0, y: 30 }}
                animate={isInView ? { opacity: 1, y: 0 } : {}}
                transition={{ duration: 0.4, delay: index * 0.12 }}
              >
                <Card className="border-0 bg-card h-full">
                  <CardContent className="p-6 md:p-8">
                    <div className="flex items-center justify-between mb-5">
                      <span className="font-heading font-bold text-3xl text-foreground/10">{step.number}</span>
                      <div className={`p-2 ${c.gradientSubtle} border ${c.border}`}>
                        <Icon className={`w-5 h-5 ${c.text}`} />
                      </div>
                    </div>
                    <h3 className="font-heading font-semibold text-sm text-foreground mb-2">{step.title}</h3>
                    <p className="text-xs text-muted-foreground font-body leading-relaxed">{step.desc}</p>
                  </CardContent>
                </Card>
              </motion.div>
            );
          })}
        </div>
      </div>
    </section>
  );
};

/* ===== SUBPAGE TESTIMONIALS ===== */
export const SubpageBewertungen = ({ color, reviews }) => {
  const ref = useRef(null);
  const isInView = useInView(ref, { once: true, margin: "-80px" });
  const c = colorConfigs[color];

  return (
    <section className="section-padding bg-card" ref={ref}>
      <div className="container-wide">
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-16 mb-0">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={isInView ? { opacity: 1, y: 0 } : {}}
            transition={{ duration: 0.5 }}
          >
            <span className="text-xs font-heading uppercase tracking-widest text-muted-foreground mb-3 block">
              Bewertungen
            </span>
            <h2 className="font-heading font-bold text-3xl sm:text-4xl lg:text-5xl text-foreground mb-4 leading-tight">
              {"Was sagen Kunden \u00fcber uns?"}
            </h2>
            <div className="bg-background border border-border p-6 mt-6">
              <div className="flex items-center gap-3 mb-2">
                <span className="font-heading font-bold text-3xl text-foreground">4.9</span>
                <div className="flex gap-0.5">
                  {[1,2,3,4,5].map(i => (
                    <Star key={i} className="w-4 h-4 fill-brand-orange text-brand-orange" />
                  ))}
                </div>
              </div>
              <p className="text-xs text-muted-foreground font-body">Google Bewertungen · Stand Juni 2026</p>
            </div>
          </motion.div>

          <div className="lg:col-span-2 grid grid-cols-1 md:grid-cols-2 gap-px bg-border">
            {reviews.map((review, index) => (
              <motion.div
                key={review.author}
                initial={{ opacity: 0, y: 20 }}
                animate={isInView ? { opacity: 1, y: 0 } : {}}
                transition={{ duration: 0.4, delay: index * 0.1 }}
              >
                <Card className={`border-0 border-l-2 ${c.borderLeft} bg-background h-full`}>
                  <CardContent className="p-6 md:p-8 flex flex-col h-full">
                    <div className="flex gap-0.5 mb-4">
                      {[1,2,3,4,5].map(i => (
                        <Star key={i} className="w-3 h-3 fill-brand-orange text-brand-orange" />
                      ))}
                    </div>
                    <Quote className="w-5 h-5 text-muted-foreground/20 mb-3" />
                    <p className="text-sm text-foreground font-body leading-relaxed mb-6 flex-1">
                      {"\u201E"}{review.quote}{"\u201C"}
                    </p>
                    <div className="flex items-center gap-3">
                      <div className={`w-9 h-9 ${c.badge} flex items-center justify-center`}>
                        <span className="font-heading font-bold text-[10px]">
                          {review.author.split(' ').map(n => n[0]).join('')}
                        </span>
                      </div>
                      <div>
                        <p className="font-heading font-semibold text-sm text-foreground">
                          {review.author}
                        </p>
                        <p className="text-xs text-muted-foreground font-body">
                          {review.company}
                        </p>
                      </div>
                    </div>
                  </CardContent>
                </Card>
              </motion.div>
            ))}
          </div>
        </div>
      </div>
    </section>
  );
};

/* ===== SUBPAGE CONTACT FORM ===== */
export const SubpageKontakt = ({ color, title }) => {
  const ref = useRef(null);
  const isInView = useInView(ref, { once: true, margin: "-80px" });
  const c = colorConfigs[color];
  const [submitted, setSubmitted] = useState(false);
  const [formData, setFormData] = useState({ name: "", email: "", phone: "", message: "" });

  const handleSubmit = async (e) => {
    e.preventDefault();
    const { submitContactForm } = await import("@/lib/contactForm");
    const success = await submitContactForm(formData);
    if (success) setSubmitted(true);
  };

  return (
    <section className="section-padding bg-background" ref={ref} id="kontakt">
      <div className="container-wide">
        <div className="grid grid-cols-1 lg:grid-cols-5 gap-0">
          <motion.div
            initial={{ opacity: 0, x: -30 }}
            animate={isInView ? { opacity: 1, x: 0 } : {}}
            transition={{ duration: 0.6 }}
            className="lg:col-span-2 bg-card border border-border lg:border-r-0 p-8 md:p-12 flex flex-col"
          >
            <div className={`w-12 h-1.5 ${c.gradient} mb-6`} />
            <h2 className="font-heading font-bold text-3xl sm:text-4xl text-foreground mb-4 leading-tight">
              {title || "Kontakt"}
              <span className="text-muted-foreground block">aufnehmen.</span>
            </h2>
            <p className="text-sm text-muted-foreground font-body leading-relaxed mb-8">
              Senden Sie uns Ihre Anfrage und wir erstellen Ihnen ein unverbindliches Angebot.
            </p>
            <div className="space-y-3 mb-8">
              {["Kostenlose Erstberatung", "Schnelle R\u00fcckmeldung", "Unverbindlich"].map((item) => (
                <div key={item} className="flex items-center gap-3">
                  <div className={`w-5 h-5 ${c.gradient} flex items-center justify-center`}>
                    <Check className="w-3 h-3 text-foreground" />
                  </div>
                  <span className="text-sm text-foreground font-body">{item}</span>
                </div>
              ))}
            </div>

            <div className="mt-auto space-y-4 pt-8 border-t border-border">
              <div className="flex items-center gap-3">
                <div className="p-2 bg-secondary"><Phone className="w-4 h-4 text-muted-foreground" /></div>
                <div>
                  <p className="text-xs text-muted-foreground font-body">Telefon</p>
                  <p className="text-sm text-foreground font-body font-medium">+49 (0) 531 87 46 10</p>
                </div>
              </div>
              <div className="flex items-center gap-3">
                <div className="p-2 bg-secondary"><Mail className="w-4 h-4 text-muted-foreground" /></div>
                <div>
                  <p className="text-xs text-muted-foreground font-body">E-Mail</p>
                  <p className="text-sm text-foreground font-body font-medium">info@freitag-bs.de</p>
                </div>
              </div>
              <div className="flex items-center gap-3">
                <div className="p-2 bg-secondary"><Clock className="w-4 h-4 text-muted-foreground" /></div>
                <div>
                  <p className="text-xs text-muted-foreground font-body">{"\u00D6"}ffnungszeiten</p>
                  <p className="text-sm text-foreground font-body font-medium">Mo - Fr: 10:00 - 14:00</p>
                </div>
              </div>
            </div>
          </motion.div>

          <motion.div
            initial={{ opacity: 0, x: 30 }}
            animate={isInView ? { opacity: 1, x: 0 } : {}}
            transition={{ duration: 0.6, delay: 0.2 }}
            className="lg:col-span-3"
          >
            {submitted ? (
              <div className="bg-card border border-border p-12 flex flex-col items-center justify-center h-full min-h-[400px]">
                <div className={`w-16 h-16 ${c.gradient} flex items-center justify-center mb-6`}>
                  <Check className="w-8 h-8 text-foreground" />
                </div>
                <h3 className="font-heading font-bold text-2xl text-foreground mb-3">Vielen Dank!</h3>
                <p className="text-sm text-muted-foreground font-body text-center mb-6">Wir melden uns bald bei Ihnen.</p>
                <Button variant="outline" onClick={() => { setSubmitted(false); setFormData({ name: "", email: "", phone: "", message: "" }); }}>
                  Neue Anfrage
                </Button>
              </div>
            ) : (
              <form onSubmit={handleSubmit} className="bg-card border border-border p-8 lg:p-12 space-y-6 h-full">
                <div>
                  <h3 className="font-heading font-bold text-xl text-foreground mb-2">Nachricht senden</h3>
                  <p className="text-xs text-muted-foreground font-body">F{"u\u0308"}llen Sie das Formular aus und wir melden uns.</p>
                </div>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-6">
                  <div className="space-y-2">
                    <label className="text-xs font-heading uppercase tracking-widest text-muted-foreground">Name *</label>
                    <Input required value={formData.name} onChange={(e) => setFormData({ ...formData, name: e.target.value })} className="bg-background border-border text-foreground font-body h-11" placeholder="Max Mustermann" />
                  </div>
                  <div className="space-y-2">
                    <label className="text-xs font-heading uppercase tracking-widest text-muted-foreground">E-Mail *</label>
                    <Input type="email" required value={formData.email} onChange={(e) => setFormData({ ...formData, email: e.target.value })} className="bg-background border-border text-foreground font-body h-11" placeholder="max@beispiel.de" />
                  </div>
                </div>
                <div className="space-y-2">
                  <label className="text-xs font-heading uppercase tracking-widest text-muted-foreground">Telefon</label>
                  <Input type="tel" value={formData.phone} onChange={(e) => setFormData({ ...formData, phone: e.target.value })} className="bg-background border-border text-foreground font-body h-11" placeholder="+49 531 87 46 10" />
                </div>
                <div className="space-y-2">
                  <label className="text-xs font-heading uppercase tracking-widest text-muted-foreground">Nachricht *</label>
                  <Textarea required rows={5} value={formData.message} onChange={(e) => setFormData({ ...formData, message: e.target.value })} className="bg-background border-border text-foreground font-body resize-none" placeholder="Beschreiben Sie Ihr Vorhaben..." />
                </div>
                <label className="flex items-start gap-3 cursor-pointer">
                  <input type="checkbox" required className="mt-1 accent-brand-blue" />
                  <span className="text-xs text-muted-foreground font-body leading-relaxed">
                    {"Ich habe die "}
                    <a href="/datenschutz" target="_blank" className="underline text-brand-blue-light hover:text-foreground">{"Datenschutzerkl\u00e4rung"}</a>
                    {" gelesen und bin mit der Verarbeitung meiner Daten einverstanden. *"}
                  </span>
                </label>
                <Button variant={c.btnVariant} size="lg" className="w-full" type="submit">
                  Nachricht senden <ArrowRight className="w-4 h-4" />
                </Button>
              </form>
            )}
          </motion.div>
        </div>
      </div>
    </section>
  );
};

/* ===== SUBPAGE FAQ ===== */
export const SubpageFAQ = ({ color, faqs }) => {
  const ref = useRef(null);
  const isInView = useInView(ref, { once: true, margin: "-80px" });

  return (
    <section className="section-padding bg-card" ref={ref}>
      <div className="container-wide">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-16">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={isInView ? { opacity: 1, y: 0 } : {}}
            transition={{ duration: 0.5 }}
          >
            <span className="text-xs font-heading uppercase tracking-widest text-muted-foreground mb-3 block">
              H{"a\u0308"}ufige Fragen
            </span>
            <h2 className="font-heading font-bold text-3xl sm:text-4xl lg:text-5xl text-foreground mb-6">
              FAQ
            </h2>
            <p className="text-sm text-muted-foreground font-body leading-relaxed">
              Hier finden Sie Antworten auf die am h{"a\u0308"}ufigsten gestellten Fragen.
            </p>
          </motion.div>

          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={isInView ? { opacity: 1, y: 0 } : {}}
            transition={{ duration: 0.5, delay: 0.2 }}
          >
            <Accordion type="single" collapsible className="w-full">
              {faqs.map((faq, index) => (
                <AccordionItem key={index} value={`item-${index}`} className="border-border">
                  <AccordionTrigger className="font-heading font-semibold text-sm text-foreground hover:no-underline py-5">
                    {faq.question}
                  </AccordionTrigger>
                  <AccordionContent className="text-sm text-muted-foreground font-body leading-relaxed pb-5">
                    {faq.answer}
                  </AccordionContent>
                </AccordionItem>
              ))}
            </Accordion>
          </motion.div>
        </div>
      </div>
    </section>
  );
};


/* ===== SUBPAGE REFERENZEN (References) ===== */
export const SubpageReferenzen = ({ color, references }) => {
  const ref = useRef(null);
  const isInView = useInView(ref, { once: true, margin: "-80px" });
  const c = colorConfigs[color];

  const defaultRefs = references || [
    { project: "Gewerbeprojekt Rhein-Main", scope: "Neubau Logistikzentrum", year: "2024" },
    { project: "Wohnanlage Westpark", scope: "12 Wohneinheiten", year: "2023" },
    { project: "Sanierung Altstadt", scope: "Denkmalgerechte Sanierung", year: "2023" },
    { project: "Industriehalle Nord", scope: "Stahl- und Hallenbau", year: "2022" },
    { project: "Schulzentrum Am Berg", scope: "Erweiterungsbau", year: "2022" },
    { project: "Parkhaus City-Center", scope: "Tiefbau & Infrastruktur", year: "2021" },
  ];

  return (
    <section className="section-padding bg-card" ref={ref}>
      <div className="container-wide">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={isInView ? { opacity: 1, y: 0 } : {}}
          transition={{ duration: 0.5 }}
          className="mb-16 max-w-2xl"
        >
          <span className="text-xs font-heading uppercase tracking-widest text-muted-foreground mb-3 block">
            Referenzen
          </span>
          <h2 className="font-heading font-bold text-3xl sm:text-4xl lg:text-5xl text-foreground mb-4 leading-tight">
            {"Ausgew\u00e4hlte Projekte."}
          </h2>
          <p className="text-sm text-muted-foreground font-body leading-relaxed">
            {"Ein Auszug unserer erfolgreich abgeschlossenen Projekte \u2013 jedes einzelne mit h\u00f6chstem Qualit\u00e4tsanspruch realisiert."}
          </p>
        </motion.div>

        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-px bg-border">
          {defaultRefs.map((item, index) => (
            <motion.div
              key={item.project}
              initial={{ opacity: 0, y: 20 }}
              animate={isInView ? { opacity: 1, y: 0 } : {}}
              transition={{ duration: 0.4, delay: index * 0.08 }}
            >
              <Card className="border-0 bg-background h-full">
                <CardContent className="p-6 md:p-8">
                  <div className="flex items-center justify-between mb-4">
                    <span className={`text-[10px] px-2.5 py-1 font-heading uppercase tracking-wider ${c.badge}`}>
                      {item.year}
                    </span>
                  </div>
                  <h3 className="font-heading font-semibold text-base text-foreground mb-2">
                    {item.project}
                  </h3>
                  <p className="text-sm text-muted-foreground font-body">
                    {item.scope}
                  </p>
                </CardContent>
              </Card>
            </motion.div>
          ))}
        </div>
      </div>
    </section>
  );
};

/* ===== SUBPAGE SEO TEXT ===== */
export const SubpageSEO = ({ color, title, text }) => {
  const ref = useRef(null);
  const isInView = useInView(ref, { once: true, margin: "-80px" });
  const c = colorConfigs[color];

  return (
    <section className="section-padding bg-card" ref={ref}>
      <div className="container-wide">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-0">
          {/* Left accent column */}
          <motion.div
            initial={{ opacity: 0, x: -20 }}
            animate={isInView ? { opacity: 1, x: 0 } : {}}
            transition={{ duration: 0.5 }}
            className="lg:col-span-4 bg-background border border-border p-8 md:p-10 flex flex-col justify-center"
          >
            <div className={`w-12 h-1 ${c.gradient} mb-6`} />
            <h2 className="font-heading font-bold text-2xl sm:text-3xl text-foreground leading-tight mb-4">
              {title}
            </h2>
            <div className={`flex items-center gap-3 mt-4 pt-4 border-t border-border`}>
              <div className={`w-10 h-10 ${c.gradientSubtle} border ${c.border} flex items-center justify-center`}>
                <FileText className={`w-4 h-4 ${c.text}`} />
              </div>
              <div>
                <p className="text-xs text-muted-foreground font-body">Teil der</p>
                <p className={`text-sm font-heading font-semibold ${c.text}`}>Freitag-Gruppe</p>
              </div>
            </div>
          </motion.div>

          {/* Right text content */}
          <motion.div
            initial={{ opacity: 0, x: 20 }}
            animate={isInView ? { opacity: 1, x: 0 } : {}}
            transition={{ duration: 0.5, delay: 0.15 }}
            className="lg:col-span-8 bg-background border border-border border-l-0 p-8 md:p-10 lg:p-12 flex flex-col justify-center"
          >
            <div className="space-y-5">
              {text.map((paragraph, index) => (
                <div key={index} className="flex gap-4">
                  <div className={`w-px shrink-0 ${index === 0 ? c.gradient : 'bg-border'} self-stretch`} />
                  <p className={`text-sm font-body leading-relaxed ${index === 0 ? 'text-foreground' : 'text-muted-foreground'}`}>
                    {paragraph}
                  </p>
                </div>
              ))}
            </div>
          </motion.div>
        </div>
      </div>
    </section>
  );
};
